import { buildApiUrl } from "../../config";
import { SlideQAResult, SlideQACallbacks, KeyFact } from "./types";
import { readModelError, fetchWithRetry } from "../../utils";
import { withCredentials } from "../apiClient";
import {
  withOptionalPptxLangfuseMetadata,
  type PptxLangfuseRunContext,
} from "../../langfuseTrace";
import { buildOpenAIImageUrl } from "../../utils/imagePayload";
import {
  SLIDE_QA_PROMPT,
  SLIDE_QA_WITH_CONTEXT_PROMPT,
  buildDeckQaUserPrompt,
} from "./promptCatalog";

async function waitBetweenSlides(signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return;
  await new Promise<void>((resolve) => {
    const timer = setTimeout(finish, 300);

    function finish() {
      clearTimeout(timer);
      signal?.removeEventListener("abort", finish);
      resolve();
    }

    signal?.addEventListener("abort", finish, { once: true });
  });
}

export async function reviewDeckForQA(
  slideImages: { index: number; base64: string }[],
  callbacks: SlideQACallbacks,
  signal?: AbortSignal,
  langfuseContext?: PptxLangfuseRunContext
): Promise<void> {
  const results: SlideQAResult[] = [];
  const accumulatedFacts: KeyFact[] = [];

  for (let i = 0; i < slideImages.length; i++) {
    if (signal?.aborted) {
      return;
    }
    const { index, base64 } = slideImages[i];
    callbacks.onProgress(i + 1, slideImages.length);
    if (signal?.aborted) {
      return;
    }

    const useContextPrompt = accumulatedFacts.length > 0;
    const prompt = useContextPrompt ? SLIDE_QA_WITH_CONTEXT_PROMPT : SLIDE_QA_PROMPT;

    const userText = buildDeckQaUserPrompt({
      slideIndex: index,
      totalSlides: slideImages.length,
      priorFacts: useContextPrompt ? accumulatedFacts : undefined,
    });
    const imageUrl = buildOpenAIImageUrl(base64);
    if (!imageUrl) {
      // Without a valid image the QA review would silently degrade to a
      // text-only request and produce a meaningless result. Surface it as an
      // error for this slide instead.
      console.warn(`[DeckQA] Skipping slide ${index + 1}: no valid image payload to review.`);
      const result: SlideQAResult = {
        slideIndex: index,
        issues: [],
        overallScore: 0,
        summary: "Error reviewing slide: no valid image was available for review.",
      };
      results.push(result);
      if (signal?.aborted) {
        return;
      }
      callbacks.onSlideResult(result);
      continue;
    }
    const content = [
      { type: "input_image", image_url: imageUrl },
      { type: "input_text", text: userText },
    ];

    try {
      const response = await fetchWithRetry(
        buildApiUrl("/api/ai/openai/responses"),
        withOptionalPptxLangfuseMetadata(
          withCredentials({
            method: "POST",
            signal,
            body: JSON.stringify({
              model: "gpt-5.5-2026-04-23",
              instructions: prompt,
              input: [
                {
                  role: "user",
                  content,
                },
              ],
              reasoning: { effort: "none" },
              text: { format: { type: "json_object" } },
              stream: false,
              service_tier: "priority",
            }),
          }),
          langfuseContext,
          { phase: "qa" }
        ),
        2,
        "Vision-Review"
      );

      if (signal?.aborted) {
        return;
      }
      if (!response.ok) {
        const errorMessage = await readModelError(response, "Slide QA failed");
        if (signal?.aborted) {
          return;
        }
        results.push({
          slideIndex: index,
          issues: [],
          overallScore: 0,
          summary: `Error reviewing slide: ${errorMessage}`,
        });
        continue;
      }

      const data = await response.json();
      if (signal?.aborted) {
        return;
      }
      let fullContent = "";
      let reasoningText = "";

      if (data.output && Array.isArray(data.output)) {
        for (const item of data.output) {
          if (item.type === "reasoning" && item.summary) {
            for (const s of item.summary) {
              if (s.type === "summary_text" && s.text) {
                reasoningText += s.text;
              }
            }
          }
          if (item.type === "message" && item.content) {
            for (const c of item.content) {
              if (c.type === "output_text" && c.text) {
                fullContent = c.text;
              }
            }
          }
        }
      }

      if (signal?.aborted) {
        return;
      }
      if (reasoningText && callbacks.onThinking) {
        callbacks.onThinking(index, reasoningText);
      }

      if (signal?.aborted) {
        return;
      }
      if (fullContent.trim()) {
        try {
          // Try to extract JSON from the response (AI sometimes wraps it in markdown code blocks)
          let jsonContent = fullContent.trim();
          
          // Remove markdown code blocks if present
          const jsonMatch = jsonContent.match(/```(?:json)?\s*([\s\S]*?)```/);
          if (jsonMatch) {
            jsonContent = jsonMatch[1].trim();
          }
          
          // Try to find JSON object in the response
          const jsonStartIndex = jsonContent.indexOf("{");
          const jsonEndIndex = jsonContent.lastIndexOf("}");
          if (jsonStartIndex !== -1 && jsonEndIndex > jsonStartIndex) {
            jsonContent = jsonContent.slice(jsonStartIndex, jsonEndIndex + 1);
          }

          const qaResult = JSON.parse(jsonContent) as Omit<SlideQAResult, "slideIndex">;

          if (qaResult.keyFacts && qaResult.keyFacts.length > 0) {
            for (const fact of qaResult.keyFacts) {
              accumulatedFacts.push({ ...fact, slideIndex: index });
            }
          }

          const result: SlideQAResult = {
            slideIndex: index,
            overallScore: qaResult.overallScore || 0,
            summary: qaResult.summary || "No summary provided",
            issues: qaResult.issues || [],
            keyFacts: qaResult.keyFacts?.map((f) => ({ ...f, slideIndex: index })) || [],
          };
          results.push(result);
          if (signal?.aborted) {
            return;
          }
          callbacks.onSlideResult(result);
        } catch (parseError) {
          if (signal?.aborted) {
            return;
          }
          console.error(`[DeckQA] Failed to parse AI response for slide ${index + 1}:`, fullContent.slice(0, 500));
          results.push({
            slideIndex: index,
            issues: [],
            overallScore: 0,
            summary: `Could not parse AI response`,
          });
        }
      } else {
        results.push({
          slideIndex: index,
          issues: [],
          overallScore: 0,
          summary: "Empty response from AI",
        });
      }

      if (i < slideImages.length - 1) {
        await waitBetweenSlides(signal);
        if (signal?.aborted) {
          return;
        }
      }
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      results.push({
        slideIndex: index,
        issues: [],
        overallScore: 0,
        summary: `Error: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }

  if (signal?.aborted) {
    return;
  }
  callbacks.onComplete(results);
}
